document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.okendo-reviews-wrapper');
  if (!container) return;

  const storeId = container.dataset.storeId;
  const productId = container.dataset.productId;
  const reviewsList = container.querySelector('.reviews-list');
  const loadMoreBtn = container.querySelector('.load-more-btn');
  const reviewsLoading = container.querySelector('.reviews-loading');

  let nextUrl = null;
  let hasMore = true;
  const limit = 5;

  async function fetchReviews(useProduct = true, customUrl = null) {
    try {
      let url;
      if (customUrl) {
        // nextUrl from Okendo API might be missing /v1, so ensure proper URL construction
        if (customUrl.startsWith('/v1/')) {
          url = `https://api.okendo.io${customUrl}`;
        } else if (customUrl.startsWith('/stores/')) {
          url = `https://api.okendo.io/v1${customUrl}`;
        } else {
          // Fallback: assume it's a complete relative path
          url = `https://api.okendo.io${customUrl}`;
        }
      } else if (useProduct && productId) {
        url = `https://api.okendo.io/v1/stores/${storeId}/products/shopify-${productId}/reviews?limit=${limit}&orderBy=date%20desc`;
      } else {
        url = `https://api.okendo.io/v1/stores/${storeId}/reviews?limit=${limit}&orderBy=date%20desc`;
      }

      const response = await fetch(url);
      const data = await response.json();

      // Update nextUrl for pagination
      nextUrl = data?.nextUrl || null;

      return data?.reviews || [];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }
  }

  async function fetchTotalReviews() {
    try {
      // Always fetch site review aggregate for site-wide totals
      const siteUrl = `https://api.okendo.io/v1/stores/${storeId}/review_aggregate`;
      const siteResponse = await fetch(siteUrl);
      const siteData = await siteResponse.json();

      let totalReviews = 0;
      let averageScore = 0;

      if (siteData?.reviewAggregate) {
        const aggregate = siteData.reviewAggregate;
        totalReviews = aggregate.reviewCount || 0;
        averageScore = aggregate.averageRating || 0;
      }

      // If aggregate doesn't provide average or returns 0, calculate from actual reviews
      if (averageScore === 0 && totalReviews > 0) {
        const reviewsUrl = `https://api.okendo.io/v1/stores/${storeId}/reviews?limit=25&orderBy=date%20desc`;
        const reviewsResponse = await fetch(reviewsUrl);
        const reviewsData = await reviewsResponse.json();

        if (reviewsData?.reviews && reviewsData.reviews.length > 0) {
          const ratings = reviewsData.reviews.map(review => review.rating).filter(rating => rating > 0);
          if (ratings.length > 0) {
            averageScore = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
          }
        }
      }

      return {
        total_review: totalReviews,
        average_score: averageScore
      };

    } catch (error) {
      console.error('Error fetching reviews summary:', error);
      return {
        total_review: 0,
        average_score: 0
      };
    }
  }

  function updateSummary(data) {
    if (!data) {
      return;
    }

    const averageEl = container.querySelector('.average');
    const totalReviewsEl = container.querySelector('.total-reviews');
    const starsEl = container.querySelector('.stars');

    if (averageEl) {
      averageEl.textContent = data.average_score.toFixed(1);
    }
    if (totalReviewsEl) {
      const count = data.total_review;
      totalReviewsEl.textContent = `(${count} ${count === 1 ? 'Review' : 'Reviews'})`;
    }

    // Update star display based on average rating
    if (starsEl) {
      const rating = Math.round(data.average_score);
      const filledStars = '★'.repeat(rating);
      const emptyStars = '☆'.repeat(5 - rating);
      starsEl.textContent = filledStars + emptyStars;
    }

  }

  // Cache for product images to avoid duplicate API calls
  const productImageCache = {};

  async function renderReview(review) {
    const date = new Date(review.dateCreated).toLocaleDateString();
    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);

    // Check if review has images and get the appropriate URL
    const hasImages = review.media && review.media.length > 0;
    let reviewImage = null;

    if (hasImages) {
      const media = review.media[0];
      // Use largeUrl for better quality, fall back to thumbnailUrl, then fullSizeUrl
      reviewImage = media.largeUrl || media.thumbnailUrl || media.fullSizeUrl;
    }

    // Use productImageUrl as fallback if no review image
    const imageToShow = reviewImage || review.productImageUrl;

    return `
      <div class="review-item">
        ${imageToShow ? `
          <div class="review-image-container">
            <img src="${imageToShow}" alt="${review.title || 'Review image'}" class="review-image" loading="lazy" />
          </div>
        ` : ''}
        <div class="review-content-wrapper">
          <div class="review-customer-info">
            <div class="review-stars">${stars}</div>
            <div class="review-author">${review.reviewer?.displayName || 'Anonymous'}</div>
            <div class="review-date">${date}</div>
            <div class="review-product">
              ${review.productName && review.productHandle ?
        `<a href="/products/${review.productHandle}" class="review-product-link">${review.productName}</a>` :
        (review.productName || '')
      }
            </div>
          </div>
          <div class="review-details">
            <div class="review-title">${review.title || ''}</div>
            <div class="review-text">${review.body || ''}</div>
          </div>
        </div>
      </div>
    `;
  }

  let isFetchingSiteReviews = false;
  let hasMoreSiteReviews = true;
  let siteNextUrl = null;

  async function loadMoreReviews() {
    if (!hasMore) return;

    try {
      let reviews = [];

      if (!isFetchingSiteReviews) {
        // Try to fetch product reviews
        if (nextUrl !== null) {
          // We have more product reviews to load
          reviews = await fetchReviews(true, nextUrl);

          // If we got fewer than limit, supplement with site reviews
          if (reviews.length < limit) {
            isFetchingSiteReviews = true;
            const remainingNeeded = limit - reviews.length;
            const siteReviews = await fetchSiteReviews(remainingNeeded);
            reviews = [...reviews, ...siteReviews];
          }
        } else {
          // Check if this is the first load or if we've exhausted product reviews
          const isFirstLoad = reviewsList.children.length === 0;

          if (isFirstLoad) {
            // First load: get initial product reviews
            reviews = await fetchReviews(true);
          } else {
            // No more product reviews, switch to site reviews
            isFetchingSiteReviews = true;
            reviews = await fetchSiteReviews(limit);
          }
        }

        // If this is first load and we got fewer than limit, supplement with site reviews
        if (reviewsList.children.length === 0 && reviews.length < limit) {
          isFetchingSiteReviews = true;
          const remainingNeeded = limit - reviews.length;
          const siteReviews = await fetchSiteReviews(remainingNeeded);
          reviews = [...reviews, ...siteReviews];
        }
      } else {
        // We're fetching site reviews
        reviews = await fetchSiteReviews(limit);
      }

      if (reviews.length > 0) {
        // Render all reviews
        const reviewElements = await Promise.all(reviews.map(async (review) => {
          return await renderReview(review);
        }));

        reviewsList.insertAdjacentHTML('beforeend', reviewElements.join(''));

        // Check if we have more reviews to load
        const hasMoreProduct = !isFetchingSiteReviews && nextUrl !== null;
        const hasMoreSite = isFetchingSiteReviews && hasMoreSiteReviews;

        // Show button if we have more reviews OR if we got a full batch (indicating more might be available)
        const shouldShowButton = hasMoreProduct || hasMoreSite || (reviews.length >= limit);
        loadMoreBtn.style.display = shouldShowButton ? 'block' : 'none';

        // Only set hasMore to false if we're sure there are no more reviews
        if (!hasMoreProduct && !hasMoreSite && reviews.length < limit) {
          hasMore = false;
        }
      } else {
        hasMore = false;
        loadMoreBtn.style.display = 'none';
      }

      if (reviewsLoading) {
        reviewsLoading.style.display = 'none';
      }
    } catch (error) {
      console.error('Error loading more reviews:', error);
      if (reviewsLoading) {
        reviewsLoading.textContent = 'Error loading reviews. Please try again later.';
      }
    }
  }

  async function fetchSiteReviews(requestedLimit = 5) {
    if (!hasMoreSiteReviews) return [];

    try {
      let url;
      if (siteNextUrl) {
        // nextUrl from Okendo API might be missing /v1, so ensure proper URL construction
        if (siteNextUrl.startsWith('/v1/')) {
          url = `https://api.okendo.io${siteNextUrl}`;
        } else if (siteNextUrl.startsWith('/stores/')) {
          url = `https://api.okendo.io/v1${siteNextUrl}`;
        } else {
          // Fallback: assume it's a complete relative path
          url = `https://api.okendo.io${siteNextUrl}`;
        }
      } else {
        url = `https://api.okendo.io/v1/stores/${storeId}/reviews?limit=${requestedLimit}&orderBy=date%20desc`;
      }

      const response = await fetch(url);
      const data = await response.json();

      // Update siteNextUrl for pagination
      siteNextUrl = data?.nextUrl || null;

      // Only set hasMoreSiteReviews to false if we got fewer reviews than requested AND no nextUrl
      const reviewsReceived = data?.reviews || [];
      if (!siteNextUrl && reviewsReceived.length < requestedLimit) {
        hasMoreSiteReviews = false;
      }

      return reviewsReceived;
    } catch (error) {
      console.error('Error fetching site reviews:', error);
      return [];
    }
  }

  async function initialize() {
    try {
      // Set up load more button first
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', loadMoreReviews);
        // Initially show the button, it will be hidden if no reviews are available
        loadMoreBtn.style.display = 'block';
      }

      // Load and update the summary
      const summary = await fetchTotalReviews();
      updateSummary(summary);

      // Load initial reviews
      await loadMoreReviews();
    } catch (error) {
      console.error('Error initializing reviews:', error);
      if (reviewsLoading) {
        reviewsLoading.textContent = 'Error loading reviews. Please refresh the page to try again.';
      }
      if (loadMoreBtn) {
        loadMoreBtn.style.display = 'none';
      }
    }
  }

  // Initialize the component
  initialize();
});

